/**
 * Datas, tamanhos e nomes — sempre em PT-PT e sempre no fuso do utilizador.
 *
 * `Intl` faz isto melhor do que qualquer biblioteca de datas e vem no browser:
 * zero bytes no bundle. Os formatadores são criados uma vez porque construir um
 * `Intl.DateTimeFormat` é caro e uma lista de mensagens chama isto centenas de
 * vezes por scroll.
 */

const time = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });

const dayLong = new Intl.DateTimeFormat("pt-BR", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const dayShort = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const stamp = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DAY_MS = 86_400_000;

/** Meia-noite local do dia de uma data — a chave para agrupar por dia. */
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export const formatTime = (iso: string): string => time.format(new Date(iso));

export const formatStamp = (iso: string): string => stamp.format(new Date(iso));

/** "Hoje" / "Ontem" / "quinta-feira, 14 de Agosto de 2026". */
export function formatDay(iso: string): string {
  const date = new Date(iso);
  const delta = startOfDay(new Date()) - startOfDay(date);
  if (delta === 0) return "Hoje";
  if (delta === DAY_MS) return "Ontem";
  return dayLong.format(date);
}

export const sameDay = (a: string, b: string): boolean => startOfDay(new Date(a)) === startOfDay(new Date(b));

/** Cabeçalho de mensagem: hora se é de hoje, data curta se é antiga. */
export function formatMessageStamp(iso: string): string {
  const date = new Date(iso);
  const delta = startOfDay(new Date()) - startOfDay(date);
  if (delta === 0) return `Hoje às ${time.format(date)}`;
  if (delta === DAY_MS) return `Ontem às ${time.format(date)}`;
  return `${dayShort.format(date)} às ${time.format(date)}`;
}

const UNITS = ["B", "KB", "MB", "GB"] as const;

export function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${UNITS[unit]}`;
}

/** Duração em segundos → "1:04:22" / "4:22". */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
